import React, { useEffect, useMemo, useState } from 'react';
import { Undo2, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBillingUtils } from '@/hooks/useBillingUtils';
import { refundInvoicePayment, getInvoicePayments, type RefundRow, type InvoicePayment } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import type { Invoice } from '@/types';

type RefundMode = 'cash' | 'upi' | 'card' | 'netbanking' | 'cheque' | 'other';

// Only modes that exist identically on both sides auto-map when a specific
// payment is picked — 'advance' and 'other' have no direct refund-mode match.
const PAYMENT_MODE_TO_REFUND_MODE: Partial<Record<string, RefundMode>> = {
  cash: 'cash', upi: 'upi', card: 'card', netbanking: 'netbanking', cheque: 'cheque',
};

interface RefundModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onRefunded?: () => void;
}

export function RefundModal({ isOpen, onClose, invoice, onRefunded }: RefundModalProps) {
  const [amount, setAmount]                   = useState(0);
  const [refundMode, setRefundMode]           = useState<RefundMode>('cash');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [upiApp, setUpiApp]                   = useState('');
  const [bankName, setBankName]               = useState('');
  const [reason, setReason]                   = useState('');
  const [loading, setLoading]                 = useState(false);
  const [payments, setPayments]               = useState<InvoicePayment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState('');
  const { formatCurrency } = useBillingUtils();

  // invoice.paid_amount is already NET of prior refunds (see invoice_payment_summary
  // view) — it IS the refundable ceiling. invoice.refunded_amount is shown for context.
  const refundable = Number(invoice?.paid_amount || 0);
  const previouslyRefunded = Number(invoice?.refunded_amount || 0);

  const selectedPayment = useMemo(
    () => payments.find(p => p.id === selectedPaymentId) || null,
    [payments, selectedPaymentId]
  );
  // When a specific payment is targeted, the refund can't exceed what's left
  // refundable on THAT payment, even if the invoice as a whole allows more.
  const effectiveCap = selectedPayment
    ? Math.min(refundable, Number(selectedPayment.refundable_amount))
    : refundable;

  // Reset on open/invoice change — amount starts at 0 so a refund must be a
  // deliberate entry, not a pre-filled "full amount" one click away from firing.
  useEffect(() => {
    if (!isOpen) return;
    setAmount(0);
    setSelectedPaymentId('');
    setPayments([]);
    if (invoice?.id) {
      setLoadingPayments(true);
      getInvoicePayments(invoice.id)
        .then(rows => setPayments(Array.isArray(rows) ? rows.filter(p => Number(p.refundable_amount) > 0) : []))
        .catch(() => setPayments([]))
        .finally(() => setLoadingPayments(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, invoice?.id]);

  const handleClose = () => {
    setAmount(0);
    setRefundMode('cash');
    setReferenceNumber('');
    setUpiApp('');
    setBankName('');
    setReason('');
    setSelectedPaymentId('');
    onClose();
  };

  const handleModeChange = (mode: string) => {
    setRefundMode(mode as RefundMode);
    setReferenceNumber('');
    setUpiApp('');
    setBankName('');
  };

  const handlePaymentSelect = (paymentId: string) => {
    setSelectedPaymentId(paymentId);
    const picked = paymentId ? payments.find(p => p.id === paymentId) : null;
    if (picked) {
      setAmount(Math.min(refundable, Number(picked.refundable_amount)));
      const mappedMode = PAYMENT_MODE_TO_REFUND_MODE[picked.payment_mode];
      if (mappedMode) handleModeChange(mappedMode);
    }
  };

  const handleSubmit = async () => {
    if (!invoice || amount <= 0 || amount > effectiveCap) return;

    const target = selectedPayment
      ? `the ${selectedPayment.payment_mode} payment of ${formatCurrency(Number(selectedPayment.amount))}`
      : `invoice ${invoice.invoice_number}`;
    const confirmed = window.confirm(
      `Refund ${formatCurrency(amount)} via ${refundMode} against ${target}?\n\nThis cannot be undone.`
    );
    if (!confirmed) return;

    setLoading(true);
    try {
      const refund: RefundRow = {
        amount,
        refund_mode:      refundMode,
        payment_id:       selectedPaymentId || undefined,
        reference_number: referenceNumber || undefined,
        upi_app:          upiApp          || undefined,
        bank_name:        bankName        || undefined,
        reason:           reason          || undefined,
      };
      await refundInvoicePayment(invoice.id, invoice.clinic_id, refund);
      toast({ title: 'Refund processed', description: `${formatCurrency(amount)} refunded for ${invoice.invoice_number}` });
      handleClose();
      onRefunded?.();
    } catch (err: any) {
      toast({ title: 'Refund failed', description: err.message || 'Could not process refund', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-destructive" />
              Refund Payment
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="medical-section p-4 bg-muted/30 rounded-lg space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Refundable Amount:</span>
                <span className="font-semibold text-foreground">{formatCurrency(refundable)}</span>
              </div>
              {previouslyRefunded > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Already refunded:</span>
                    <span className="text-muted-foreground">{formatCurrency(previouslyRefunded)}</span>
                  </div>
              )}
            </div>

            {(loadingPayments || payments.length > 0) && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Refund Against</label>
                  <Select
                      value={selectedPaymentId || '__general__'}
                      onValueChange={(v) => handlePaymentSelect(v === '__general__' ? '' : v)}
                      disabled={loadingPayments}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__general__">General — not tied to a specific payment</SelectItem>
                      {payments.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.payment_mode.charAt(0).toUpperCase() + p.payment_mode.slice(1)} — {formatCurrency(Number(p.refundable_amount))} refundable
                          </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Picking the original payment refunds through the matching channel and keeps records precise.
                  </p>
                </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Amount to Refund *</label>
              <Input
                  type="number"
                  min="0"
                  max={effectiveCap}
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(Math.min(effectiveCap, Math.max(0, parseFloat(e.target.value) || 0)))}
                  placeholder="Enter amount"
              />
              {amount > effectiveCap && (
                  <div className="text-xs text-destructive flex items-center gap-1 mt-1.5">
                    <AlertCircle className="w-3 h-3" />
                    {selectedPayment ? 'Amount exceeds this payment\'s refundable balance' : 'Amount exceeds refundable balance'}
                  </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Refund Method</label>
              <Select value={refundMode} onValueChange={handleModeChange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="netbanking">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {refundMode === 'upi' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">UPI Transaction ID</label>
                  <Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)}
                         placeholder="e.g. 123456789012" className="font-mono text-sm" />
                </div>
            )}

            {refundMode === 'cheque' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Cheque Number</label>
                    <Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)}
                           placeholder="Enter cheque number" className="font-mono text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Bank Name</label>
                    <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. SBI, HDFC, ICICI" />
                  </div>
                </div>
            )}

            {refundMode === 'netbanking' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Transaction Reference</label>
                  <Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)}
                         placeholder="NEFT/RTGS reference number" className="font-mono text-sm" />
                </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Reason (optional)</label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Service cancelled, overcharge" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={handleClose} disabled={loading}>Cancel</Button>
            <Button
                onClick={handleSubmit}
                disabled={loading || amount <= 0 || amount > effectiveCap}
                variant="destructive"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</> : 'Process Refund'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
  );
}
